import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { format, addDays } from 'date-fns';

interface DemoContextType {
  isDemoMode: boolean;
  demoData: any;
  saveDemoData: (key: string, data: any) => void;
  getDemoData: (key: string) => any;
  clearAllDemoData: () => void;
  initializeDemoData: () => void;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

const DEMO_STORAGE_KEY = 'health_tracker_demo_data';

// Generate sample demo data
const generateInitialDemoData = () => {
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  
  return {
    goals: {
      id: 'demo-goal-1',
      user_id: 'demo-user',
      daily_cal_target: 2000,
      daily_protein_target: 150,
      daily_workout_min_target: 45,
      target_weight: 165,
      created_at: new Date().toISOString(),
    },
    meal_plans: [
      {
        id: 'demo-meal-1',
        user_id: 'demo-user',
        date: todayStr,
        meal_type: 'breakfast',
        description: 'Oatmeal with berries and almonds',
        calories: 350,
        protein: 12,
        carbs: 55,
        fat: 8,
        location: 'Home',
        completed: false,
      },
      {
        id: 'demo-meal-2',
        user_id: 'demo-user',
        date: todayStr,
        meal_type: 'lunch',
        description: 'Grilled chicken salad with quinoa',
        calories: 450,
        protein: 35,
        carbs: 40,
        fat: 15,
        location: 'Work',
        completed: false,
      },
      {
        id: 'demo-meal-3',
        user_id: 'demo-user',
        date: format(addDays(today, 1), 'yyyy-MM-dd'),
        meal_type: 'breakfast',
        description: 'Greek yogurt with granola',
        calories: 300,
        protein: 20,
        carbs: 35,
        fat: 8,
        location: 'Home',
        completed: false,
      },
    ],
    workout_plans: [
      {
        id: 'demo-workout-1',
        user_id: 'demo-user',
        date: todayStr,
        workout_type: 'Cardio',
        description: 'Morning run',
        duration_minutes: 30,
        calories_burned: 300,
        location: 'Outdoor',
        intensity: 'Moderate',
        completed: false,
      },
      {
        id: 'demo-workout-2',
        user_id: 'demo-user',
        date: format(addDays(today, 1), 'yyyy-MM-dd'),
        workout_type: 'Strength',
        description: 'Upper body workout',
        duration_minutes: 45,
        calories_burned: 250,
        location: 'Gym',
        intensity: 'High',
        completed: false,
      },
    ],
    log_entries: [
      {
        id: 'demo-log-1',
        user_id: 'demo-user',
        date: format(addDays(today, -1), 'yyyy-MM-dd'),
        entry_type: 'Meal',
        description: 'Salmon with vegetables',
        calories_in: 500,
        protein_g: 40,
        created_at: new Date().toISOString(),
      },
      {
        id: 'demo-log-2',
        user_id: 'demo-user',
        date: format(addDays(today, -1), 'yyyy-MM-dd'),
        entry_type: 'Workout',
        description: 'Evening yoga',
        duration_min: 30,
        calories_burned: 150,
        created_at: new Date().toISOString(),
      },
    ],
    vitals: [
      {
        id: 'demo-vital-1',
        user_id: 'demo-user',
        date: format(addDays(today, -7), 'yyyy-MM-dd'),
        weight_lb: 175,
        created_at: new Date().toISOString(),
      },
      {
        id: 'demo-vital-2',
        user_id: 'demo-user',
        date: format(addDays(today, -3), 'yyyy-MM-dd'),
        weight_lb: 173,
        created_at: new Date().toISOString(),
      },
      {
        id: 'demo-vital-3',
        user_id: 'demo-user',
        date: todayStr,
        weight_lb: 170,
        created_at: new Date().toISOString(),
      },
    ],
  };
};

export function DemoProvider({ children }: { children: ReactNode }) {
  const [isDemoMode] = useState(true);
  const [demoData, setDemoData] = useState<any>({});

  useEffect(() => {
    // Load demo data from localStorage
    const savedData = localStorage.getItem(DEMO_STORAGE_KEY);
    if (savedData) {
      try {
        setDemoData(JSON.parse(savedData));
      } catch (e) {
        console.error('Failed to parse demo data');
        initializeDemoData();
      }
    } else {
      initializeDemoData();
    }
  }, []);

  const initializeDemoData = () => {
    const initialData = generateInitialDemoData();
    setDemoData(initialData);
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(initialData));
  };

  const saveDemoData = (key: string, data: any) => {
    const newData = { ...demoData, [key]: data };
    setDemoData(newData);
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(newData));
  };

  const getDemoData = (key: string) => {
    return demoData[key] || [];
  };

  const clearAllDemoData = () => {
    const freshData = generateInitialDemoData();
    setDemoData(freshData);
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(freshData));
  };

  return (
    <DemoContext.Provider
      value={{
        isDemoMode,
        demoData,
        saveDemoData,
        getDemoData,
        clearAllDemoData,
        initializeDemoData,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const context = useContext(DemoContext);
  if (context === undefined) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
}
